import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createManualBooking } from '../api';
import VehicleSearchField from './VehicleSearchField';
import RenterAutocomplete from './RenterAutocomplete';

interface Props {
  onSaved: () => void;
  onClose: () => void;
}

type Form = {
  vehicle_id: number | null;
  renter_name: string;
  renter_phone: string;
  renter_email: string;
  starts_at: string;
  ends_at: string;
  invoice_number: string;
  invoice_date: string;
  rental_amount: string;
  dynamic_pricing: string;
  km_extra_amount: string;
  km_overage_amount: string;
  excess_reduction: string;
  excess_risk_amount: string;
  fuel_adjustment: string;
  late_return_fee: string;
  extra_driver_fee: string;
  foreign_driving_fee: string;
  handling_fees: string;
  discount: string;
  payout: string;
  km_start: string;
  km_end: string;
  km_included: string;
  km_extra: string;
  fuel_start_pct: string;
  fuel_end_pct: string;
  notes: string;
};

const empty: Form = {
  vehicle_id: null,
  renter_name: '', renter_phone: '', renter_email: '',
  starts_at: '', ends_at: '',
  invoice_number: '', invoice_date: '',
  rental_amount: '', dynamic_pricing: '',
  km_extra_amount: '', km_overage_amount: '',
  excess_reduction: '', excess_risk_amount: '',
  fuel_adjustment: '', late_return_fee: '',
  extra_driver_fee: '', foreign_driving_fee: '',
  handling_fees: '', discount: '', payout: '',
  km_start: '', km_end: '', km_included: '', km_extra: '',
  fuel_start_pct: '', fuel_end_pct: '',
  notes: '',
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500 mb-2">{title}</h3>
      {children}
    </div>
  );
}

const fieldInputCls =
  'w-full border border-ink-200 bg-cream-50 rounded-lg px-2.5 py-1.5 text-sm text-ink-900 placeholder:text-[#a89d8a] focus:outline-none focus:border-sage-500 focus:ring-2 focus:ring-sage-500/30 transition-colors';

function Field<K extends keyof Form>({
  label, k, type = 'number', step = '0.01', form, set,
}: {
  label: string;
  k: K;
  type?: string;
  step?: string;
  form: Form;
  set: (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}) {
  return (
    <div>
      <label className="block text-xs text-ink-500 mb-1">{label}</label>
      <input
        type={type}
        step={type === 'number' ? step : undefined}
        value={form[k] as string}
        onChange={set(k)}
        className={fieldInputCls}
        placeholder="—"
      />
    </div>
  );
}

export default function ManualBookingModal({ onSaved, onClose }: Props) {
  const { t } = useTranslation();
  const [form, setForm] = useState<Form>(empty);
  const [vehicleLabel, setVehicleLabel] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (key: keyof Form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(prev => ({ ...prev, [key]: e.target.value }));

  const handleVehicle = (v: { id: number; registration_number: string }) => {
    setForm(prev => ({ ...prev, vehicle_id: v.id }));
    setVehicleLabel(v.registration_number);
  };

  const handleSave = async () => {
    if (!form.starts_at) { setError(t('manual_booking.error_starts_at')); return; }
    setSaving(true);
    setError(null);
    const toNum = (s: string) => s === '' ? null : parseFloat(s);
    const toInt = (s: string) => s === '' ? null : parseInt(s, 10);
    try {
      await createManualBooking({
        vehicle_id:          form.vehicle_id,
        renter_name:         form.renter_name         || null,
        renter_phone:        form.renter_phone        || null,
        renter_email:        form.renter_email        || null,
        starts_at:           form.starts_at.replace('T', ' ') + ':00',
        ends_at:             form.ends_at ? form.ends_at.replace('T', ' ') + ':00' : null,
        invoice_number:      form.invoice_number      || null,
        invoice_date:        form.invoice_date        || null,
        rental_amount:       toNum(form.rental_amount),
        dynamic_pricing:     toNum(form.dynamic_pricing),
        km_extra_amount:     toNum(form.km_extra_amount),
        km_overage_amount:   toNum(form.km_overage_amount),
        excess_reduction:    toNum(form.excess_reduction),
        excess_risk_amount:  toNum(form.excess_risk_amount),
        fuel_adjustment:     toNum(form.fuel_adjustment),
        late_return_fee:     toNum(form.late_return_fee),
        extra_driver_fee:    toNum(form.extra_driver_fee),
        foreign_driving_fee: toNum(form.foreign_driving_fee),
        handling_fees:       toNum(form.handling_fees),
        discount:            toNum(form.discount),
        payout:              toNum(form.payout),
        km_start:            toInt(form.km_start),
        km_end:              toInt(form.km_end),
        km_included:         toInt(form.km_included),
        km_extra:            toInt(form.km_extra),
        fuel_start_pct:      toInt(form.fuel_start_pct),
        fuel_end_pct:        toInt(form.fuel_end_pct),
        notes:               form.notes               || null,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Fejl');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-panel max-w-lg">

        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="modal-title">{t('manual_booking.title')}</h2>
          <button onClick={onClose} className="modal-close">&times;</button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[70vh] space-y-5">
          {error && <p className="text-sm text-[var(--color-error)]">{error}</p>}

          <Section title={t('rentals.col_vehicle')}>
            {vehicleLabel ? (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-800">{vehicleLabel}</span>
                <button
                  onClick={() => { setVehicleLabel(null); setForm(prev => ({ ...prev, vehicle_id: null })); }}
                  className="text-xs text-gray-400 hover:text-[var(--color-error)]"
                >
                  ×
                </button>
              </div>
            ) : (
              <VehicleSearchField
                placeholder={t('rentals.search_vehicle')}
                onSelect={handleVehicle}
              />
            )}
          </Section>

          <Section title={t('manual_booking.section_renter')}>
            <div className="grid grid-cols-2 gap-3">
              <RenterAutocomplete
                label={t('manual_booking.renter_name')}
                value={form.renter_name}
                onChange={v => setForm(prev => ({ ...prev, renter_name: v }))}
                onSelect={r => setForm(prev => ({
                  ...prev,
                  renter_name: r.name,
                  renter_phone: r.phone ?? prev.renter_phone,
                  renter_email: r.email ?? prev.renter_email,
                }))}
              />
              <Field form={form} set={set} label={t('manual_booking.renter_phone')} k="renter_phone" type="text" />
              <div className="col-span-2">
                <Field form={form} set={set} label={t('manual_booking.renter_email')} k="renter_email" type="email" />
              </div>
            </div>
          </Section>

          <Section title={t('manual_booking.section_period')}>
            <div className="grid grid-cols-2 gap-3">
              <Field form={form} set={set} label={t('manual_booking.starts_at')} k="starts_at" type="datetime-local" />
              <Field form={form} set={set} label={t('manual_booking.ends_at')}   k="ends_at"   type="datetime-local" />
            </div>
          </Section>

          <Section title={t('rental.detail_invoice_no')}>
            <div className="grid grid-cols-2 gap-3">
              <Field form={form} set={set} label={t('rental.detail_invoice_no')} k="invoice_number" type="text" />
              <Field form={form} set={set} label={t('rental.col_date')}          k="invoice_date"   type="date" />
            </div>
          </Section>

          <Section title={t('rental.detail_economy')}>
            <div className="grid grid-cols-2 gap-3">
              <Field form={form} set={set} label={t('rental.col_rental')}             k="rental_amount" />
              <Field form={form} set={set} label={t('rental.col_dynamic_pricing')}    k="dynamic_pricing" />
              <Field form={form} set={set} label={t('rental.col_km_extra_amount')}    k="km_extra_amount" />
              <Field form={form} set={set} label={t('rental.col_km_overage_amount')}  k="km_overage_amount" />
              <Field form={form} set={set} label={t('rental.col_excess')}             k="excess_reduction" />
              <Field form={form} set={set} label={t('rental.col_excess_risk')}        k="excess_risk_amount" />
              <Field form={form} set={set} label={t('rental.col_fuel_adjustment')}    k="fuel_adjustment" />
              <Field form={form} set={set} label={t('rental.col_late_return_fee')}    k="late_return_fee" />
              <Field form={form} set={set} label={t('rental.detail_extra_driver_fee')} k="extra_driver_fee" />
              <Field form={form} set={set} label={t('rental.detail_foreign_fee')}     k="foreign_driving_fee" />
              <Field form={form} set={set} label={t('rental.col_fee')}                k="handling_fees" />
              <Field form={form} set={set} label={t('rental.col_discount')}           k="discount" />
              <Field form={form} set={set} label={t('rental.col_payout')}             k="payout" />
            </div>
          </Section>

          <Section title={t('rental.detail_km')}>
            <div className="grid grid-cols-2 gap-3">
              <Field form={form} set={set} label={t('rental.detail_km_start')}           k="km_start"    step="1" />
              <Field form={form} set={set} label={t('rental.detail_km_end')}             k="km_end"      step="1" />
              <Field form={form} set={set} label={t('rental.detail_km_included')}        k="km_included" step="1" />
              <Field form={form} set={set} label={t('rental.detail_km_extra_purchased')} k="km_extra"    step="1" />
            </div>
          </Section>

          <Section title={t('rental.detail_fuel')}>
            <div className="grid grid-cols-2 gap-3">
              <Field form={form} set={set} label={t('rental.detail_fuel_start')} k="fuel_start_pct" step="1" />
              <Field form={form} set={set} label={t('rental.detail_fuel_end')}   k="fuel_end_pct"   step="1" />
            </div>
          </Section>

          <Section title={t('events.notes')}>
            <textarea
              value={form.notes}
              onChange={set('notes')}
              rows={3}
              className={fieldInputCls}
              placeholder="—"
            />
          </Section>
        </div>

        <div className="flex justify-end gap-3 px-6 pb-6 pt-2">
          <button onClick={onClose} disabled={saving} className="btn btn-secondary">
            {t('modal.cancel')}
          </button>
          <button onClick={handleSave} disabled={saving} className="btn btn-primary disabled:opacity-50">
            {saving ? '...' : t('manual_booking.save_btn')}
          </button>
        </div>

      </div>
    </div>
  );
}
